// Capacity (BL-057): how much of an arrival window a company's technicians have free. Pure interval
// arithmetic over instants (epoch milliseconds), shared by the server's capacity.availability and,
// later, the iPad and online booking: working hours, less what takes a tech away (time off,
// holidays, non-job time), less what they're booked on.

/** The arrival windows the office books into, in company hours (Book's chips; the capacity's windows). */
export const ARRIVAL_WINDOWS: readonly (readonly [number, number])[] = [
  [8, 10],
  [10, 12],
  [12, 14],
  [14, 16],
  [16, 18],
];

/** A stretch of time, start inclusive, end exclusive, in epoch milliseconds. */
export interface Span {
  start: number;
  end: number;
}

/** The spans in order, overlapping and touching ones joined, empty ones gone. */
export function merged(spans: readonly Span[]): Span[] {
  const out: Span[] = [];
  for (const s of [...spans].filter((x) => x.end > x.start).sort((a, b) => a.start - b.start)) {
    const last = out.at(-1);
    if (last && s.start <= last.end) last.end = Math.max(last.end, s.end);
    else out.push({ ...s });
  }
  return out;
}

/** The parts of `spans` inside [from, to). */
export function clipped(spans: readonly Span[], from: number, to: number): Span[] {
  return merged(spans.map((s) => ({ start: Math.max(s.start, from), end: Math.min(s.end, to) })));
}

/** `spans` with `cuts` taken out. */
export function without(spans: readonly Span[], cuts: readonly Span[]): Span[] {
  let out = merged(spans);
  for (const c of merged(cuts))
    out = out.flatMap((s) =>
      c.end <= s.start || c.start >= s.end
        ? [s]
        : [
            ...(c.start > s.start ? [{ start: s.start, end: c.start }] : []),
            ...(c.end < s.end ? [{ start: c.end, end: s.end }] : []),
          ],
    );
  return out;
}

/** How many whole minutes the spans cover. */
export function minutesIn(spans: readonly Span[]): number {
  return Math.floor(merged(spans).reduce((m, s) => m + (s.end - s.start), 0) / 60_000);
}

/**
 * Whether a visit of `minutes` can start inside the window and run in one free stretch: the tech
 * arrives within the window, and the work may run past its end (as an arrival window means).
 */
export function fitsVisit(free: readonly Span[], window: Span, minutes: number): boolean {
  const need = minutes * 60_000;
  return merged(free).some((f) => {
    const start = Math.max(f.start, window.start);
    return start < window.end && start + need <= f.end;
  });
}

export interface TechDaySpans {
  /** Their working hours that day. */
  hours: Span[];
  /** What takes them away: time off, a holiday, non-job time. */
  away: Span[];
  /** The visits they're on. */
  booked: Span[];
}

export interface WindowCapacity {
  /** Working minutes inside the window, less time away. */
  totalMinutes: number;
  /** Of those, the minutes the techs are booked on. */
  bookedMinutes: number;
  /** Minutes of visits in the window nobody is on yet (they'll take someone's time). */
  unassignedMinutes: number;
  /** total − booked − unassigned, never below 0: the hours still to book. */
  openMinutes: number;
  /** The techs with some free time in the window. */
  freeTechIds: string[];
  /** Whether some tech can take a visit of the given length starting in it; null with no length. */
  fits: boolean | null;
}

/** Where two sets of spans overlap. */
export function overlap(a: readonly Span[], b: readonly Span[]): Span[] {
  return merged(merged(b).flatMap((x) => clipped(a, x.start, x.end)));
}

/** One window's capacity from each eligible tech's day (and the unassigned visits in it). */
export function windowCapacity(
  window: Span,
  techs: ReadonlyMap<string, TechDaySpans>,
  unassigned: readonly Span[],
  visitMinutes: number | null,
): WindowCapacity {
  let total = 0;
  let booked = 0;
  let fits = false;
  const freeTechIds: string[] = [];
  for (const [id, t] of techs) {
    const working = without(t.hours, t.away);
    const inWindow = clipped(working, window.start, window.end);
    const tTotal = minutesIn(inWindow);
    const tBooked = minutesIn(overlap(inWindow, t.booked));
    total += tTotal;
    booked += tBooked;
    if (tTotal > tBooked) freeTechIds.push(id);
    if (visitMinutes !== null && fitsVisit(without(working, t.booked), window, visitMinutes)) fits = true;
  }
  // each visit nobody is on yet takes its own tech's time: two at once are twice the minutes
  const unassignedMinutes = unassigned.reduce(
    (m, u) => m + minutesIn(clipped([u], window.start, window.end)),
    0,
  );
  return {
    totalMinutes: total,
    bookedMinutes: booked,
    unassignedMinutes,
    openMinutes: Math.max(0, total - booked - unassignedMinutes),
    freeTechIds,
    fits: visitMinutes === null ? null : fits,
  };
}

/** The office's change to a window (BL-057 phase 2): close it, or add or take off minutes. */
export interface Adjustment {
  closed: boolean;
  minutes: number;
}

/**
 * A window's capacity after the office's changes, and whether it's over the ideal: booked and
 * unassigned past `idealPercent` of its (changed) total. A closed window is full: nothing open, nobody
 * free, nothing fits. Minutes added stand for time with no named tech (an on-call), so who's free and
 * what fits stay the techs' own.
 */
export function adjustedCapacity(
  c: WindowCapacity,
  adjustments: readonly Adjustment[],
  idealPercent: number,
): WindowCapacity & { isOverIdeal: boolean } {
  const used = c.bookedMinutes + c.unassignedMinutes;
  if (adjustments.some((a) => a.closed))
    return {
      ...c,
      openMinutes: 0,
      freeTechIds: [],
      fits: c.fits === null ? null : false,
      isOverIdeal: false,
    };
  const total = Math.max(0, c.totalMinutes + adjustments.reduce((m, a) => m + a.minutes, 0));
  return {
    ...c,
    totalMinutes: total,
    openMinutes: Math.max(0, total - used),
    isOverIdeal: total > 0 ? used * 100 > total * idealPercent : used > 0,
  };
}
